<template>
  <div class="container full-screen">

    <h3 class="d-flex justify-content-between">
      Application
      <button @click="refresh" class="btn btn-outline-info">Refresh App</button>
    </h3>
    <p v-if="!updateAvailable">Already up to date on <code>{{ version }}</code>.</p>
    <p v-else>Refresh to Update.</p>

    <h3 class="mt-5 d-flex justify-content-between">
      Logs
      <button v-if="log.length >= 1" @click="resetLog" class="btn btn-outline-warning">Reset Logs</button>
    </h3>
    <ul class="list-unstyled">
      <li v-if="log.length < 1"><p>No logs to display.</p></li>
      <li v-for="{timestamp, message} in log" :key="`${timestamp}-${message}`" class="mb-1">
        <p>
          <span class="text-muted">{{ timestamp.toLocaleString() }}</span><br>
          {{ message }}
        </p>
      </li>
    </ul>

    <h3 class="mt-5 d-flex justify-content-between">
      Storage
      <button @click="persist" v-if="hasStorageApi && ! storage.persisted" class="btn btn-outline-info">Allow Persistent
        Storage
      </button>
    </h3>
    <ul class="list-unstyled">
      <li>
        <p class="d-inline-flex align-content-center">
          Persistent Storage
          <code>
            <icon class="ml-2" :name="storage.persisted ? 'check' : 'x'"/>
          </code>
        </p>
      </li>
      <li v-if="hasStorageApi">
        <p>Used <code>{{ storageConsumption.usage }}kb / {{ storageConsumption.quota }}kb</code> of available storage.</p>
      </li>
      <li v-else><p>This browser does not support the Storage API.</p></li>
    </ul>

    <div class="progress" v-if="storageConsumption && storageConsumption.percent !== '0%'">
      <div class="progress-bar"
           role="progressbar"
           aria-valuemin="0"
           aria-valuemax="100"
           :aria-valuenow="storageConsumption.percent"
           :width="storageConsumption.percent"
      ></div>
    </div>

    <h3 class="mt-5 d-flex justify-content-between">
      Database
      <button @click="exportData" class="btn btn-outline-info">Export</button>
    </h3>
    <ul class="list-unstyled">
      <li>Driver: <code>{{ database.driver }}</code></li>
      <li>Supported Drivers:
        <code v-for="driver in supportedDrivers" :key="driver">{{ driver }} </code>
      </li>
    </ul>

    <h3 class="mt-5 d-flex justify-content-between">
      Notifications
      <button @click="toggleNotifications" class="btn btn-outline-info" v-if="! notifications">Allow</button>
    </h3>
    <p>Notifications <code>enabled</code>.</p>

  </div>
</template>

<style scoped>
  p {
    margin-bottom: 0;
  }
</style>

<script>
  /* eslint-disable no-console */
  import storage from 'localforage'

  export default {
    data: () => ({
      storage: {
        estimate: null,
        persisted: null
      },
      database: {
        entries: {},
        driver: null,
      },
    }),
    mounted () {
      this.getStorageDetails()
      this.getDatabase()
    },
    methods: {
      async toggleNotifications () {
        return Notification.requestPermission()
      },
      async getDatabase () {
        await storage.iterate((value, key) => {
          if (key !== 'log') {
            this.database.entries[key] = value
          }
        })
        this.database.driver = storage.driver()
      },
      async getStorageDetails () {
        try {
          this.storage.estimate = await window.navigator.storage.estimate()
          this.storage.persisted = await window.navigator.storage.persisted()
        } catch (e) {
          this.$store.commit('log', e)
        }
      },
      async persist () {
        try {
          await window.navigator.storage.persist()
          this.getStorageDetails()
        } catch (e) {
          this.$store.commit('log', e)
        }
      },
      refresh () {
        return window.location.reload()
      },
      resetLog () {
        this.$store.commit('resetLog')
      },
      exportData () {
        const now = new Date()
        
        // Create export data with all stored entries
        const exportObject = {
          exportDate: now.toISOString(),
          version: this.$store.state.version,
          data: this.database.entries
        }
        
        // Convert to JSON string
        const dataStr = JSON.stringify(exportObject, null, 2)
        
        // Create blob and download link
        const blob = new Blob([dataStr], { type: 'application/json' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `chronicle-export-${now.toISOString().split('T')[0]}.json`
        
        // Trigger download
        document.body.appendChild(link)
        link.click()
        
        // Clean up
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
        
        this.$store.commit('log', 'Data exported successfully')
      }
    },
    computed: {
      notifications () {
        return !!Notification.permission
      },
      version () {
        return `${this.$store.state.version}`.slice(0, 7)
      },
      supportedDrivers () {
        const drivers = {
          IndexedDB: storage.supports(storage.INDEXEDDB),
          LocalStorage: storage.supports(storage.LOCALSTORAGE),
          WebSQL: storage.supports(storage.WEBSQL),
        }
        return Object.keys(drivers).filter((value, key, keys) => drivers[keys[key]])
      },
      hasStorageApi () {
        return !!window.navigator['storage']
      },
      updateAvailable () {
        return this.$store.state.updateAvailable
      },
      log () {
        return this.$store.state.log
      },
      storageConsumption () {
        if (this.storage.estimate === null) {
          return false
        }

        const estimate = {...this.storage.estimate}

        Object.keys(estimate).map(key => estimate[key] = Math.round(estimate[key] / 1000))

        estimate.percent = Math.round(estimate.usage / estimate.quota * 100) + '%'

        return estimate
      }
    }
  }
</script>
